import React from 'react';
import { Helmet } from 'react-helmet';
import Layout from '@layouts/layout';
import SectionLayout from '@layouts/section-layout';
import algoliasearch from 'algoliasearch/lite';
import aa from 'search-insights';
import FilterBlock from '@blocks/filter-block/filter-block';
import StyledFilterMenu from '@components/filter-menu/filter-menu';
import UtilityBar from '@components/utility-bar/utility-bar';
import ContentHubResults from '../design-system/src/components/content-hub-results/content-hub-results';
import CustomSortBy from '../design-system/src/blocks/custom-sortby/custom-sortby';
import CustomStats from '../design-system/src/components/search/search-stats';
import StyledAppliedFiltersBlock from '../design-system/src/blocks/applied-filters-block/applied-filters-block';
import StyledSearchBox from '../design-system/src/components/search/search-box';
import StyledTopicsFilter from '../design-system/src/components/topics-filter/topics-filter';
import StyledSearchIntro from '../design-system/src/components/search/search-intro';
import styled from '@emotion/styled/macro';
import vars from '@variables';
import { orderQueryString } from '../utils/string-helper-functions';

import {
  Configure,
  CurrentRefinements,
  InstantSearch,
} from 'react-instantsearch';
import { CustomSearchProvider } from '../design-system/src/components/search/CustomSearchContext';
import { TranslateUnconnected } from '../design-system/src/utils/translation-functions';
import {
  createNavbarLocaleLinks,
  generateSearchHrefLangLinks,
} from '../utils/generate-url-functions';
import { getFooterData } from '../utils/translation-functions';
import { history } from 'instantsearch.js/es/lib/routers';
import { orderBy } from 'lodash';

import { DelimitedArrayParam, useQueryParam } from 'use-query-params';
import { useLocation } from '@reach/router';

/* ----------------------------------------------------------------
   NEW: true SSR – make the request query available at render time
   This lets the server return the correct canonical/robots/title.
-----------------------------------------------------------------*/
export async function getServerData({ query, url }) {
  // Build a compact object; we’ll rehydrate to URLSearchParams in the component.
  // url is the requested path + query (e.g. /explore?topics=Business&sortBy=dateDesc)
  const ssr = {
    query, // the parsed query object (e.g. { topics: 'Business', sortBy: 'dateDesc' })
    url, // the requested path + query (no domain)
  };

  // Conservative cache headers (you can tune later):
  const headers = {
    'Cache-Control':
      'public, max-age=60, s-maxage=600, stale-while-revalidate=86400',
  };

  return { props: { ssr }, headers, status: 200 };
}

// ----------------------
// Locale templating utils
// ----------------------
function formatTpl(tpl, vars = {}) {
  return tpl.replace(/\{(\w+)\}/g, (_, k) => vars[k] ?? '');
}

const TEMPLATES = {
  'en-GB': {
    // Titles
    title_topic: 'Circular economy & {topic}',
    title_topic_ct: 'Circular economy & {topic} {ctypePlural}',
    title_cee: 'Circular economy explained',
    title_cee_ct: 'Circular economy explained {ctypePlural}',

    // H1
    h1_topic: 'Explore our circular economy {topic} content',
    h1_topic_ct: 'Explore our circular economy {topic} {ctypePlural}',
    h1_cee: 'Explore our circular economy explained content',
    h1_cee_ct: 'Explore our circular economy explained {ctypePlural}',

    // Descriptions / intros
    desc_topic_ct:
      'Explore thought-provoking {ctypePlural} from the Ellen MacArthur Foundation that delve into the connection between the circular economy and {topic}. Discover expert insights and solutions for a regenerative future.',
    desc_topic:
      'Delve into the connection between the circular economy and {topic}. Discover expert insights and solutions for a regenerative future from the Ellen MacArthur Foundation.',
    desc_cee:
      'Discover the circular economy explained. Hear from leading thinkers, practitioners, and changemakers as they explore innovative solutions, real-world examples and fresh perspectives that drive systems change and build a regenerative future.',
    desc_cee_ct:
      'Explore thought-provoking {ctypePlural} from the Ellen MacArthur Foundation that delve into the circular economy. Discover expert insights and solutions for a regenerative future.',

    intro_topic_ct:
      'Discover how the circular economy intersects with {topic} through our curated collection of {ctypePlural}. Hear from leading thinkers, practitioners, and changemakers as they explore innovative solutions, real-world examples and fresh perspectives that drive systems change and build a regenerative future.',
    intro_topic:
      'Discover how the circular economy intersects with {topic}. Hear from leading thinkers, practitioners, and changemakers as they explore innovative solutions, real-world examples and fresh perspectives that drive systems change and build a regenerative future.',
    intro_cee:
      'Discover the circular economy. Hear from leading thinkers, practitioners, and changemakers as they explore innovative solutions, real-world examples and fresh perspectives that drive systems change and build a regenerative future.',
    intro_cee_ct:
      'Discover the circular economy through our curated collection of {ctypePlural}. Hear from leading thinkers, practitioners, and changemakers as they explore innovative solutions, real-world examples and fresh perspectives that drive systems change and build a regenerative future.',
    intro_topic_ct_col:
      'Discover how the circular economy intersects with {topic} through our curated {ctypePlural}. Hear from leading thinkers, practitioners, and changemakers as they explore innovative solutions, real-world examples and fresh perspectives that drive systems change and build a regenerative future.',
  },

  es: {
    title_topic: 'Economía circular y {topic}',
    title_topic_ct: 'Economía circular y {topic} {ctypePlural}',
    title_cee: 'Economía circular explicada',
    title_cee_ct: 'Economía circular explicada {ctypePlural}',

    h1_topic: 'Explora nuestro contenido sobre economía circular y {topic}',
    // gendered H1s for topic + contentType
    h1_topic_ct_masc:
      'Explora nuestros {ctypePlural} sobre economía circular y {topic}',
    h1_topic_ct_fem:
      'Explora nuestras {ctypePlural} sobre economía circular y {topic}',
    // CEE H1s
    h1_cee: 'Explora nuestro contenido sobre economía circular explicada',
    h1_cee_ct_masc:
      'Explora nuestros {ctypePlural} sobre economía circular explicada',
    h1_cee_ct_fem:
      'Explora nuestras {ctypePlural} sobre economía circular explicada',

    desc_topic_ct:
      'Explora {ctypePlural} estimulantes de la Fundación Ellen MacArthur que profundizan en la relación entre la economía circular y {topic}. Descubre ideas expertas y soluciones para un futuro regenerativo.',
    desc_topic:
      'Profundiza en la relación entre la economía circular y {topic}. Descubre ideas expertas y soluciones para un futuro regenerativo de la Fundación Ellen MacArthur.',
    desc_cee:
      'Descubre la economía circular explicada. Conoce a líderes de pensamiento, profesionales y agentes del cambio mientras exploran soluciones innovadoras, ejemplos reales y nuevas perspectivas que impulsan el cambio sistémico y construyen un futuro regenerativo.',
    desc_cee_ct:
      'Explora {ctypePlural} estimulantes de la Fundación Ellen MacArthur que profundizan en la economía circular. Descubre ideas expertas y soluciones para un futuro regenerativo.',

    intro_topic_ct:
      'Descubre cómo la economía circular se cruza con {topic} a través de nuestra colección de {ctypePlural}. Conoce a líderes de pensamiento, profesionales y agentes del cambio mientras exploran soluciones innovadoras, ejemplos reales y nuevas perspectivas que impulsan el cambio sistémico y construyen un futuro regenerativo.',
    intro_topic:
      'Descubre cómo la economía circular se cruza con {topic}. Conoce a líderes de pensamiento, profesionales y agentes del cambio mientras exploran soluciones innovadoras, ejemplos reales y nuevas perspectivas que impulsan el cambio sistémico y construyen un futuro regenerativo.',
    intro_cee:
      'Descubre la economía circular. Conoce a líderes de pensamiento, profesionales y agentes del cambio mientras exploran soluciones innovadoras, ejemplos reales y nuevas perspectivas que impulsan el cambio sistémico y construyen un futuro regenerativo.',
    intro_cee_ct:
      'Descubre la economía circular a través de nuestra colección de {ctypePlural}. Conoce a líderes de pensamiento, profesionales y agentes del cambio mientras exploran soluciones innovadoras, ejemplos reales y nuevas perspectivas que impulsan el cambio sistémico y construyen un futuro regenerativo.',
    intro_topic_ct_col:
      'Descubre cómo la economía circular se cruza con {topic} a través de nuestra {ctypePlural}. Conoce a líderes de pensamiento, profesionales y agentes del cambio mientras exploran soluciones innovadoras, ejemplos reales y nuevas perspectivas que impulsan el cambio sistémico y construyen un futuro regenerativo.',
  },

  pt: {
    title_topic: 'Economia circular e {topic}',
    title_topic_ct: 'Economia circular e {topic} {ctypePlural}',
    title_cee: 'Economia circular explicada',
    title_cee_ct: 'Economia circular explicada {ctypePlural}',

    h1_topic: 'Explore nosso conteúdo sobre economia circular e {topic}',
    h1_topic_ct_masc:
      'Explore nossos {ctypePlural} sobre economia circular e {topic}',
    h1_topic_ct_fem:
      'Explore nossas {ctypePlural} sobre economia circular e {topic}',
    // CEE H1s
    h1_cee: 'Explore nosso conteúdo sobre economia circular explicada',
    h1_cee_ct_masc:
      'Explore nossos {ctypePlural} sobre economia circular explicada',
    h1_cee_ct_fem:
      'Explore nossas {ctypePlural} sobre economia circular explicada',

    desc_topic_ct:
      'Explore {ctypePlural} instigantes da Ellen MacArthur Foundation que aprofundam a relação entre a economia circular e {topic}. Descubra insights de especialistas e soluções para um futuro regenerativo.',
    desc_topic:
      'Aprofunde-se na relação entre a economia circular e {topic}. Descubra insights de especialistas e soluções para um futuro regenerativo da Ellen MacArthur Foundation.',
    desc_cee:
      'Descubra a economia circular explicada. Ouça líderes de pensamento, profissionais e agentes de mudança enquanto exploram soluções inovadoras, exemplos reais e novas perspectivas que impulsionam a mudança sistêmica e constroem um futuro regenerativo.',
    desc_cee_ct:
      'Explore {ctypePlural} instigantes da Ellen MacArthur Foundation que aprofundam a economia circular. Descubra insights de especialistas e soluções para um futuro regenerativo.',

    intro_topic_ct:
      'Descubra como a economia circular se cruza com {topic} por meio de nossa coleção de {ctypePlural}. Ouça líderes de pensamento, profissionais e agentes de mudança enquanto exploram soluções inovadoras, exemplos reais e novas perspectivas que impulsionam a mudança sistêmica e constroem um futuro regenerativo.',
    intro_topic:
      'Descubra como a economia circular se cruza com {topic}. Ouça líderes de pensamento, profissionais e agentes de mudança enquanto exploram soluções inovadoras, exemplos reais e novas perspectivas que impulsionam a mudança sistêmica e constroem um futuro regenerativo.',
    intro_cee:
      'Descubra a economia circular. Ouça líderes de pensamento, profissionais e agentes de mudança enquanto exploram soluções inovadoras, exemplos reais e novas perspectivas que impulsionam a mudança sistêmica e constroem um futuro regenerativo.',
    intro_cee_ct:
      'Descubra a economia circular por meio de nossa coleção de {ctypePlural}. Ouça líderes de pensamento, profissionais e agentes de mudança enquanto exploram soluções inovadoras, exemplos reais e novas perspectivas que impulsionam a mudança sistêmica e constroem um futuro regenerativo.',
    intro_topic_ct_col:
      'Descubra como a economia circular se cruza com {topic} por meio de nossa {ctypePlural}. Ouça líderes de pensamento, profissionais e agentes de mudança enquanto exploram soluções inovadoras, exemplos reais e novas perspectivas que impulsionam a mudança sistêmica e constroem um futuro regenerativo.',
  },
};

function buildCopy(locale = 'en-GB', key, vars = {}) {
  const dict = TEMPLATES[locale] || TEMPLATES['en-GB'];
  const tpl = dict[key] || TEMPLATES['en-GB'][key] || '';
  return formatTpl(tpl, vars);
}

// Content-type grammatical gender for es/pt (plural labels you show to users)
const CONTENT_TYPE_GENDER = {
  es: {
    Article: 'masc',
    CaseStudy: 'masc',
    Collection: 'fem',
    Event: 'masc',
    ExternalResource: 'masc',
    LearningResource: 'masc',
    News: 'fem',
    Podcast: 'masc',
    PressRelease: 'masc',
    Project: 'masc',
    Publication: 'fem',
    Toolkit: 'fem',
    Video: 'masc',
    WorkshopResource: 'masc',
  },
  pt: {
    Article: 'masc',
    CaseStudy: 'masc',
    Collection: 'fem',
    Event: 'masc',
    ExternalResource: 'masc',
    LearningResource: 'masc',
    News: 'fem',
    Podcast: 'masc',
    PressRelease: 'masc',
    Project: 'masc',
    Publication: 'fem',
    Toolkit: 'fem',
    Video: 'masc',
    WorkshopResource: 'masc',
  },
};

// ----------------------

const ALLOWED_META_TOPICS = new Set([
  'Biodiversity',
  'Business',
  'CircularEconomyExplained',
  'BuiltEnvironment',
  'Cities',
  'Climate',
  'CriticalMinerals',
  'Design',
  'Education',
  'Fashion',
  'Finance',
  'Food',
  'Mission',
  'Network',
  'Plastics',
  'Policy',
  'Retail',
]);

const ALLOWED_META_CONTENT_TYPES = new Set([
  'Article',
  'CaseStudy',
  'Collection',
  'Event',
  'ExternalResource',
  'LearningResource',
  'News',
  'Podcast',
  'PressRelease',
  'Project',
  'Publication',
  'Toolkit',
  'Video',
  'WorkshopResource',
]);

const CONTENT_TYPE_PLURALS_EN = {
  Article: 'articles',
  CaseStudy: 'case studies',
  Collection: 'collections',
  Event: 'events',
  ExternalResource: 'external resources',
  LearningResource: 'learning resources',
  News: 'news',
  Podcast: 'podcasts',
  PressRelease: 'press releases',
  Project: 'projects',
  Publication: 'publications',
  Toolkit: 'toolkits',
  Video: 'videos',
  WorkshopResource: 'workshop resources',
};

aa('init', {
  appId: process.env.GATSBY_ALGOLIA_APP_ID,
  apiKey: process.env.GATSBY_ALGOLIA_API_KEY,
  useCookie: true,
});

const encodedRefinementsTitle = {
  resourceContentType: 'Content Type',
};

const searchClient = algoliasearch(
  process.env.GATSBY_ALGOLIA_APP_ID,
  process.env.GATSBY_ALGOLIA_API_KEY,
  process.env.GATSBY_ALGOLIA_INDEX_NAME
);

const ContentHubPage = ({ pageContext, className, serverData }) => {
  // Primary nav and footer data handling
  const footerData = getFooterData(pageContext.locale);
  const staticTranslations = pageContext.staticTranslations;

  const gtmDataLayerVars = {
    event: 'Page View',
    pageType: 'content hub',
  };
  const algoliaIndexName = process.env.GATSBY_ALGOLIA_INDEX_NAME;

  const encodedSorting = {
    dateDesc: `${algoliaIndexName}_date-desc`,
    dateAsc: `${algoliaIndexName}_date-asc`,
    aZ: `${algoliaIndexName}_A-Z`,
    zA: `${algoliaIndexName}_Z-A`,
    rel: `${algoliaIndexName}`,
  };

  const FILTERS = [
    'format',
    'topics.title',
    'resourceContentType.title',
    'geography.title',
  ];

  const encodedMenu = {
    [FILTERS[1]]: 'topics',
    [FILTERS[2]]: 'contentType',
    [FILTERS[3]]: 'geography',
  };

  const encodeSortByForURL = sortBy => {
    for (const [encodedSortKey, sortByValue] of Object.entries(
      encodedSorting
    )) {
      if (sortBy === sortByValue) {
        return encodedSortKey;
      }
    }
    return 'rel';
  };

  const decodeSortByFromURL = encodedSortKey => {
    return encodedSorting[encodedSortKey] || encodedSorting.dateDesc;
  };

  const encodedFilters = {
    AboutUs: 'About us',
    BuiltEnvironment: 'Built Environment',
    LearningResource: 'Learning resource',
    WorkshopResource: 'Workshop resource',
    CircularEconomyExplained: 'Circular economy explained',
    CaseStudy: 'Case study',
    LatinAmerica: 'Latin America',
    NorthAmerica: 'North America',
    ExternalResource: 'External Resource',
    PressRelease: 'Press Release',
    CriticalMinerals: 'Critical Minerals',
  };

  function encodeFiltersForURL(searchValues) {
    const matchingKeys = [];
    for (const searchValue of searchValues) {
      let foundMatch = false;
      for (const key in encodedFilters) {
        if (encodedFilters[key] === searchValue) {
          matchingKeys.push(key);
          foundMatch = true;
          break;
        }
      }
      if (!foundMatch) {
        matchingKeys.push(searchValue);
      }
    }
    return matchingKeys.join('_');
  }

  const decodeFiltersFromURL = filterKeys => {
    const decodedFilters = [];
    for (const filterKey of filterKeys) {
      decodedFilters.push(
        encodedFilters.hasOwnProperty(filterKey)
          ? encodedFilters[filterKey]
          : filterKey
      );
    }

    return decodedFilters;
  };

  // Map: Encoded key -> Localized label for this locale
  const topicLabelMap = {};
  [...ALLOWED_META_TOPICS].forEach(key => {
    const decoded = decodeFiltersFromURL([key])[0]; // English label
    topicLabelMap[key] = TranslateUnconnected(decoded, staticTranslations);
  });

  // Encoded contentType key -> localized label
  const contentTypeLabelMap = {};
  [...ALLOWED_META_CONTENT_TYPES].forEach(key => {
    const decoded = decodeFiltersFromURL([key])[0];
    contentTypeLabelMap[key] = TranslateUnconnected(
      decoded,
      staticTranslations
    );
  });

  const contentTypePluralLabelMap = {};
  Object.entries(CONTENT_TYPE_PLURALS_EN).forEach(([key, pluralEn]) => {
    contentTypePluralLabelMap[key] = TranslateUnconnected(
      pluralEn,
      staticTranslations
    );
  });

  // These hooks still drive Algolia UI state/routing
  const [topics] = useQueryParam('topics', DelimitedArrayParam);
  const [contentType] = useQueryParam('contentType', DelimitedArrayParam);
  const [geography] = useQueryParam('geography', DelimitedArrayParam);

  /* --------------------------------------------------------------
     Query + path selection: prefer serverData (SSR request),
     otherwise fall back to client location (CSR).
  ---------------------------------------------------------------*/
  // --- Hooks that must always be called unconditionally ---
  const clientLocation = useLocation();
  const clientPathname = clientLocation.pathname;
  const clientSearch = clientLocation.search || '';
  const clientParams = React.useMemo(
    () => new URLSearchParams(clientSearch),
    [clientSearch]
  );

  // --- Prefer serverData when present; otherwise use client values ---
  let pathname;
  let qs;
  let params;

  if (serverData?.ssr?.url) {
    // Server render: rebuild from request url & query object
    // e.g. "/explore?topics=Business&sortBy=dateDesc"
    const requestUrl = serverData.ssr.url;
    const idx = requestUrl.indexOf('?');
    pathname = idx >= 0 ? requestUrl.slice(0, idx) : requestUrl;

    // Rebuild URLSearchParams from serverData.ssr.query to be safe & consistent
    const p = new URLSearchParams();
    const qObj = serverData.ssr.query || {};
    Object.entries(qObj).forEach(([k, v]) => {
      if (Array.isArray(v)) v.forEach(val => p.append(k, String(val)));
      else if (v != null) p.set(k, String(v));
    });
    qs = p.toString() ? `?${p.toString()}` : '';
    params = p;
  } else {
    // Client render
    pathname = clientPathname;
    qs = clientSearch;
    params = clientParams;
  }

  // Read encoded keys directly (handles single or joined with '_')
  const topicsParam = params.get('topics'); // e.g. "BuiltEnvironment" or "Finance_Food"
  const topicKeysFromSearch = topicsParam
    ? topicsParam.split('_').filter(Boolean)
    : [];

  const contentTypeParam = params.get('contentType'); // e.g. "Article" or "Article_Video"
  const contentTypeKeysFromSearch = contentTypeParam
    ? contentTypeParam.split('_').filter(Boolean)
    : [];

  const hasNonTopicCtRefinements =
    !!params.get('geography') ||
    !!params.get('format') ||
    !!params.get('search');

  // Base/no-filter detection (indexable)
  const isBaseNoFilters =
    !topicsParam && !contentTypeParam && !hasNonTopicCtRefinements;

  const singleAllowedTopicKey =
    topicKeysFromSearch.length === 1 &&
    ALLOWED_META_TOPICS.has(topicKeysFromSearch[0]) &&
    !hasNonTopicCtRefinements
      ? topicKeysFromSearch[0]
      : null;

  const singleAllowedContentTypeKey =
    contentTypeKeysFromSearch.length === 1 &&
    ALLOWED_META_CONTENT_TYPES.has(contentTypeKeysFromSearch[0]) &&
    !hasNonTopicCtRefinements
      ? contentTypeKeysFromSearch[0]
      : null;

  const selectedTopicLabel = singleAllowedTopicKey
    ? decodeFiltersFromURL([singleAllowedTopicKey])[0]
    : null;

  const selectedContentTypeLabel = singleAllowedContentTypeKey
    ? decodeFiltersFromURL([singleAllowedContentTypeKey])[0]
    : null;

  // Indexable states:
  //  - base /explore with no filters
  //  - single allowed topic (± single allowed content type)
  const indexable =
    isBaseNoFilters ||
    (singleAllowedTopicKey &&
      (contentTypeKeysFromSearch.length === 0 ||
        !!singleAllowedContentTypeKey));

  function sentenceCase(str = '', locale) {
    const s = String(str).trim();
    if (!s) return '';
    return (
      s.charAt(0).toLocaleUpperCase(locale) +
      s.slice(1).toLocaleLowerCase(locale)
    );
  }

  // Localized values for templates
  const locale = pageContext.locale;
  const brandName = 'Ellen MacArthur Foundation';

  const topicLocalized = selectedTopicLabel
    ? TranslateUnconnected(selectedTopicLabel, staticTranslations)
    : '';

  const ctPluralLocalized = singleAllowedContentTypeKey
    ? contentTypePluralLabelMap[singleAllowedContentTypeKey] ||
      selectedContentTypeLabel
    : '';

  // Choose gender for es/pt when one content type is selected
  const ctGender =
    singleAllowedContentTypeKey && (locale === 'es' || locale === 'pt')
      ? CONTENT_TYPE_GENDER[locale]?.[singleAllowedContentTypeKey] || 'masc'
      : null;

  // H1
  const ssrH1 = (() => {
    if (selectedTopicLabel && selectedContentTypeLabel) {
      if (singleAllowedTopicKey === 'CircularEconomyExplained') {
        const key =
          locale === 'es' || locale === 'pt'
            ? `h1_cee_ct_${ctGender}`
            : 'h1_cee_ct';
        return sentenceCase(
          buildCopy(locale, key, { ctypePlural: ctPluralLocalized }),
          locale
        );
      }
      const key =
        locale === 'es' || locale === 'pt'
          ? `h1_topic_ct_${ctGender}`
          : 'h1_topic_ct';
      return sentenceCase(
        buildCopy(locale, key, {
          topic: topicLocalized,
          ctypePlural: ctPluralLocalized,
        }),
        locale
      );
    }
    if (selectedTopicLabel) {
      if (singleAllowedTopicKey === 'CircularEconomyExplained') {
        return sentenceCase(buildCopy(locale, 'h1_cee'), locale);
      }
      return sentenceCase(
        buildCopy(locale, 'h1_topic', { topic: topicLocalized }),
        locale
      );
    }
    return sentenceCase(pageContext.contentHubTitle, locale);
  })();

  // Meta Title (append brand outside sentenceCase)
  const ssrMetaTitle = (() => {
    if (selectedTopicLabel && selectedContentTypeLabel) {
      if (singleAllowedTopicKey === 'CircularEconomyExplained') {
        const left = buildCopy(locale, 'title_cee_ct', {
          ctypePlural: ctPluralLocalized,
        });
        return `${sentenceCase(left, locale)} - ${brandName}`;
      }
      const left = buildCopy(locale, 'title_topic_ct', {
        topic: topicLocalized,
        ctypePlural: ctPluralLocalized,
      });
      return `${sentenceCase(left, locale)} - ${brandName}`;
    }
    if (selectedTopicLabel) {
      if (singleAllowedTopicKey === 'CircularEconomyExplained') {
        const left = buildCopy(locale, 'title_cee');
        return `${sentenceCase(left, locale)} - ${brandName}`;
      }
      const left = buildCopy(locale, 'title_topic', { topic: topicLocalized });
      return `${sentenceCase(left, locale)} - ${brandName}`;
    }
    return sentenceCase(pageContext.contentHubMetaDataTitle, locale);
  })();

  // Meta Description
  const ssrMetaDescription = (() => {
    if (selectedTopicLabel && selectedContentTypeLabel) {
      if (singleAllowedTopicKey === 'CircularEconomyExplained') {
        return buildCopy(locale, 'desc_cee_ct', {
          ctypePlural: ctPluralLocalized,
        });
      }
      return buildCopy(locale, 'desc_topic_ct', {
        topic: topicLocalized,
        ctypePlural: ctPluralLocalized,
      });
    }
    if (selectedTopicLabel) {
      if (singleAllowedTopicKey === 'CircularEconomyExplained') {
        return buildCopy(locale, 'desc_cee');
      }
      return buildCopy(locale, 'desc_topic', { topic: topicLocalized });
    }
    return pageContext.contentHubMetaDataTitle; // default fallback
  })();

  // Intro
  const ssrIntroText = (() => {
    if (selectedTopicLabel && selectedContentTypeLabel) {
      if (singleAllowedTopicKey === 'CircularEconomyExplained') {
        return buildCopy(locale, 'intro_cee_ct', {
          ctypePlural: ctPluralLocalized,
        });
      }
      if (selectedContentTypeLabel === 'Collection') {
        return buildCopy(locale, 'intro_topic_ct_col', {
          topic: topicLocalized.toLowerCase(),
          ctypePlural: ctPluralLocalized,
        });
      }
      return buildCopy(locale, 'intro_topic_ct', {
        topic: topicLocalized.toLowerCase(),
        ctypePlural: ctPluralLocalized,
      });
    }
    if (selectedTopicLabel) {
      if (singleAllowedTopicKey === 'CircularEconomyExplained') {
        return buildCopy(locale, 'intro_cee');
      }
      return buildCopy(locale, 'intro_topic', {
        topic: topicLocalized.toLowerCase(),
      });
    }
    return '';
  })();

  const dynamicTitle = ssrH1;
  const dynamicMetaTitle = ssrMetaTitle;
  const indexableTopicOrCt = indexable;
  const shouldNoIndex = !indexableTopicOrCt;

  // Canonical: full URL when indexable; otherwise canonicalize to base explore
  const orderedQs = orderQueryString(qs);
  const basePath =
    pageContext.locale === 'en-GB'
      ? '/explore'
      : `/${pageContext.locale}/${pageContext.slug}`;
  const canonicalUrl = indexableTopicOrCt
    ? `${vars.baseDomain}${pathname}${orderedQs}`
    : `${vars.baseDomain}${basePath}`;

  const metaData = {
    title: dynamicTitle,
    metaTitle: dynamicMetaTitle,
    metaDescription: ssrMetaDescription,
    socialCardTitle: dynamicMetaTitle,
    socialCardDescription: pageContext.contentHubMetaDataSocialCardDescription,
    localeCode: pageContext.locale,
    robots: shouldNoIndex ? 'noindex,follow' : 'index,follow',
    canonicalLink: canonicalUrl,
  };

  const routing = {
    router: history(),
    stateMapping: {
      stateToRoute(uiState) {
        const indexUiState = uiState[algoliaIndexName];
        const urlParams = {
          search: indexUiState.query,
          sortBy: encodeSortByForURL(indexUiState.sortBy),
          format: indexUiState.menu?.format,
        };

        if (!indexUiState.refinementList) return urlParams;

        Object.keys(encodedMenu).forEach(filterKey => {
          if (indexUiState.refinementList[filterKey]) {
            urlParams[encodedMenu[filterKey]] = encodeFiltersForURL(
              indexUiState.refinementList[filterKey]
            );
          }
        });

        return urlParams;
      },
      routeToState(routeState) {
        const newState = {
          [algoliaIndexName]: {
            query: routeState.search,
            menu: {
              format: routeState.format,
            },
            refinementList: {},
            page: routeState.page,
            sortBy: decodeSortByFromURL(routeState.sortBy),
          },
        };

        if (topics) {
          newState[algoliaIndexName].refinementList[FILTERS[1]] =
            decodeFiltersFromURL(topics);
        }

        if (contentType) {
          newState[algoliaIndexName].refinementList[FILTERS[2]] =
            decodeFiltersFromURL(contentType);
        }

        if (geography) {
          newState[algoliaIndexName].refinementList[FILTERS[3]] =
            decodeFiltersFromURL(geography);
        }

        return newState;
      },
    },
  };

  const navbarLocaleLinks = createNavbarLocaleLinks(
    [
      {
        enabled: true,
        node_locale: 'en-GB',
        pageType: 'basic',
        slug: 'explore',
      },
      {
        enabled: true,
        node_locale: 'es',
        pageType: 'basic',
        slug: 'explora',
      },
      {
        enabled: true,
        node_locale: 'pt',
        pageType: 'basic',
        slug: 'saiba-mais',
      },
    ],
    pageContext.enabledLocaleCodes
  );

  return (
    <Layout
      footerData={footerData}
      className={className}
      metaData={metaData}
      gtmDataLayerVars={gtmDataLayerVars}
      hasTopPadding={true}
      isContentHub={true}
      globals={pageContext?.globals ? pageContext.globals : null}
      navbarLocaleLinks={navbarLocaleLinks}
    >
      {/* PAGE-ONLY overrides */}
      <Helmet>
        {serverData?.ssr && <meta name="x-ssr" content="1" />}
        <meta
          name="robots"
          content={shouldNoIndex ? 'noindex,follow' : 'index,follow'}
        />
        <link rel="canonical" href={metaData.canonicalLink} />
        <meta property="og:url" content={metaData.canonicalLink} />

        <link
          rel="alternate"
          hrefLang="en-GB"
          href={generateSearchHrefLangLinks(canonicalUrl, 'en')}
        />
        <link
          rel="alternate"
          hrefLang="es"
          href={generateSearchHrefLangLinks(canonicalUrl, 'es')}
        />
        <link
          rel="alternate"
          hrefLang="pt"
          href={generateSearchHrefLangLinks(canonicalUrl, 'pt')}
        />
        <link
          rel="alternate"
          hrefLang="x-default"
          href={generateSearchHrefLangLinks(canonicalUrl, 'en')}
        />
      </Helmet>
      <SectionLayout isContentHub={true}>
        <ContentWrapper>
          <CustomSearchProvider>
            <InstantSearch
              indexName={algoliaIndexName}
              searchClient={searchClient}
              routing={routing}
              insights={true}
            >
              <Configure
                filters={`locale:${pageContext.locale}`}
                hitsPerPage="20"
                clickAnalytics
                userToken={'user-1'}
              />
              <StyledSearchBox
                title={dynamicTitle}
                baseTitle={pageContext.contentHubTitle}
                baseMetaTitle={pageContext.contentHubMetaDataTitle}
                backgroundImageSrc={pageContext.contentHubSearchBoxBannerImage}
                // label maps for client localization
                topicLabelMap={topicLabelMap}
                contentTypeLabelMap={contentTypeLabelMap}
                contentTypePluralLabelMap={contentTypePluralLabelMap}
                // locale + templates + genders
                locale={pageContext.locale}
                contentTypeGenderMap={CONTENT_TYPE_GENDER}
                templates={TEMPLATES}
                // meta & intro for initial SSR
                baseMetaDescription={ssrMetaDescription}
                introTextSSR={ssrIntroText}
                brandName={brandName}
              />

              {pageContext.localeLinks.length > 0 && <UtilityBar />}
              <div className="inner-wrapper">
                <div className="filters-wrapper" aria-label="Search Filters">
                  <FilterBlock>
                    <StyledFilterMenu
                      attribute="format"
                      className="filter-menu"
                    />
                  </FilterBlock>
                  <div className="dropdown-filters-wrapper">
                    <StyledTopicsFilter
                      label="Topic"
                      attribute="topics.title"
                      limit={40}
                      showMoreLimit={50}
                      showMore={true}
                      transformItems={items => orderBy(items, 'label', 'asc')}
                    />
                    <StyledTopicsFilter
                      label="Content type"
                      attribute="resourceContentType.title"
                      limit={40}
                      showMoreLimit={50}
                      showMore={true}
                      transformItems={items => orderBy(items, 'label', 'asc')}
                    />
                    <StyledTopicsFilter
                      label="Geography"
                      attribute="geography.title"
                      limit={40}
                      showMoreLimit={50}
                      showMore={true}
                      transformItems={items => orderBy(items, 'label', 'asc')}
                    />
                    <CustomSortBy
                      items={[
                        { value: `${algoliaIndexName}_A-Z`, label: 'A-Z' },
                        { value: `${algoliaIndexName}_Z-A`, label: 'Z-A' },
                        {
                          value: `${algoliaIndexName}_date-desc`,
                          label: TranslateUnconnected(
                            'Most recent',
                            staticTranslations
                          ),
                        },
                        {
                          value: `${algoliaIndexName}_date-asc`,
                          label: TranslateUnconnected(
                            'Least recent',
                            staticTranslations
                          ),
                        },
                        {
                          value: `${algoliaIndexName}`,
                          label: TranslateUnconnected(
                            'Most relevant',
                            staticTranslations
                          ),
                        },
                      ]}
                      staticTranslations={staticTranslations}
                    />
                  </div>
                </div>

                <StyledAppliedFiltersBlock>
                  <CurrentRefinements
                    transformItems={items => {
                      const transformedItems = items.map(item => {
                        if (Array.isArray(item.refinements)) {
                          const label = item.label
                            ? item.label.split('.')[0]
                            : '';
                          item.label = encodedRefinementsTitle[label] || label; // handles resourceContentType outlier
                          item.refinements = item.refinements.map(
                            refinement => {
                              return {
                                ...refinement,
                                label: TranslateUnconnected(
                                  refinement.label.trim(),
                                  staticTranslations
                                ),
                              };
                            }
                          );
                        } else {
                          item.label = item.label.split(':')[1];
                        }

                        return {
                          ...item,
                          label: TranslateUnconnected(
                            item.label?.trim(),
                            staticTranslations
                          ),
                        };
                      });
                      transformedItems.sort((a, b) => {
                        return (
                          FILTERS.indexOf(a.attribute) -
                          FILTERS.indexOf(b.attribute)
                        );
                      });

                      transformedItems.forEach(item => {
                        if (item.attribute === FILTERS[0]) {
                          item.label = item.refinements[0].label;
                          item.refinements[0].label = null;
                        }
                        return item;
                      });

                      return transformedItems;
                    }}
                  />
                </StyledAppliedFiltersBlock>
                <StyledSearchIntro introText={ssrIntroText} />
                <CustomStats />
                <ContentHubResults />
              </div>
            </InstantSearch>
          </CustomSearchProvider>
        </ContentWrapper>
      </SectionLayout>
    </Layout>
  );
};

const ContentWrapper = styled.div`
  border: 2px solid grey;
`;

const StyledContentHub = styled(ContentHubPage)`
  .inner-wrapper {
    // display: flex;
    height: 100%;
    max-width: ${vars.mainMaxWidth};
    margin: 0 auto ${vars.spacingXl};
    padding-left: ${vars.spacingL};
    padding-right: ${vars.spacingL};

    // flex-direction: column;
  }

  .filters-wrapper {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    width: 100%;

    @media screen and (max-width: ${vars.breakPointTablet}) {
      min-height: 25rem;
    }

    div {
    }

    .dropdown-filters-wrapper {
      display: flex;
      padding-top: 3rem;

      @media screen and (max-width: ${vars.breakPointTablet}) {
        display: flex;
        flex-wrap: wrap;
        padding-top: 0;
        justify-content: space-between;
        width: 100%;

        div {
          padding-bottom: 5rem;
        }
      }
      @media screen and (max-width: ${vars.breakPointSmallDesktop}) {
        min-height: 8rem;
        padding-top: 0;
      }
    }
  }

  ${SectionLayout} {
    .section-container {
      margin-top: 0;
    }
  }
  .ais-Hits .ais-Hits-list {
    list-style: none;
  }
  .refine-sort-block {
    margin-top: 1.5rem;
    display: flex;
    justify-content: space-between;
    flex-direction: column;
    margin-left: 0;
    @media screen and (min-width: calc(${vars.breakPointTenColumn} + 1px)) and (max-width: ${vars.breakPointTablet}) {
      flex-direction: row;
      margin-top: 1.6rem;
      width: 100%;
      flex-wrap: wrap;
      margin-left: 0;
    }
    @media screen and (min-width: ${vars.breakPointTablet}) {
      flex-direction: row;
      max-height: 5rem;
      width: 100%;
      margin-left: 0;
      justify-content: flex-start;
    }
    @media screen and (min-width: ${vars.breakPointSmallDesktop}) {
      margin-left: 1.5rem;
      justify-content: flex-end;
    }
  }
  .ais-RefinementList {
    position: relative;
    display: inline-block;
    margin-right: 0;
    width: 100%;
    ${vars.fontSize2}
    color: ${vars.policyGrey};
    .ais-RefinementList-list {
      width: 100%;
      border: 1px solid ${vars.borderMidGrey};
      position: absolute;
      z-index: 99;
      background-color: white;
      border-radius: 8px;
      list-style-type: none;

      .ais-RefinementList-item {
        min-height: 3.6rem;
        width: 15rem;
      }

      .ais-sortby-item {
        width: 100%;
        padding-top: 0.7rem;
        padding-bottom: 0.7rem;
        height: 3.6rem;

        button {
          font-weight: ${vars.fontWeightRegular};
          display: flex;
          justify-content: start;
          color: black;
          background-color: transparent;

          &:disabled {
            color: #ccc;
            cursor: default;
          }
        }
      }

      .ais-RefinementList-item-showMore {
        padding: 1.2rem 0.8rem;

        .ais-RefinementList-showMore {
          background: transparent;
          width: 100%;
          display: flex;
          justify-content: space-between;

          svg {
            margin-top: 0;
          }

          span {
            font-weight: ${vars.fontWeightRegular};
            font-size: 1.4rem;
          }
        }
      }

      button {
        width: 100%;
        height: 100%;
        border: 0;
        cursor: pointer;
      }
    }
    @media screen and (min-width: calc(${vars.breakPointTenColumn} + 1px)) and (max-width: ${vars.breakPointTablet}) {
      width: 47%;
      margin-right: 0;
    }
    @media screen and (min-width: ${vars.breakPointTablet}) {
      margin-right: 1.5rem;
      width: 16rem;

      &:last-of-type {
        margin-right: 0;
      }
    }
  }

  .ais-RefinementList li {
    padding: 8px;

    .ais-RefinementList-label {
      cursor: pointer;
      display: flex;
      justify-content: start;

      .ais-RefinementList-labelText {
        font-weight: ${vars.fontWeightRegular};
        margin-left: 0.5rem;
      }
    }
  }
  .ais-RefinementList-list li:nth-of-type(1) {
    font-weight: ${vars.fontWeightRegular};
    ${vars.fontSize2};
    line-height: 2.9rem;
    padding: ${vars.spacingS};
  }
  .ais-RefinementList-list li:nth-of-type(2) {
    padding-top: 1.2rem;
  }
  .ais-SortBy {
    display: inline-block;
    vertical-align: top;
    @media screen and (max-width: ${vars.breakPointTablet}) {
      width: 100%;
    }
  }
  .ais-SortBy-option {
    font-weight: ${vars.fontWeightRegular};
    font-size: 1.2rem;
  }

  .ais-InfiniteHits {
    padding-bottom: 3rem;
    .ais-InfiniteHits-list {
      list-style: none;
      padding: 0;
      margin: 0;
      display: flex;
      flex-wrap: wrap;
    }

    .ais-InfiniteHits-loadMore {
      display: block;
      min-width: 16rem;
      margin: ${vars.spacingS} auto;
      margin-bottom: 2rem;
      padding: 1.4rem 0;
      border: 1px solid ${vars.transparentBorderGrey};
      color: ${vars.coreDarkGrey};
      background: ${vars.white};
      ${vars.fontSize2}
      font-weight: ${vars.fontWeightBold};
      text-align: center;
      text-decoration: none;
      &:first-letter {
        text-transform: uppercase;
      }
      ${vars.primaryLinkHoverTransition};
      &:hover {
        background-color: ${vars.coreDarkGrey};
        color: ${vars.white};
        cursor: pointer;
      }
      &:focus {
        outline: none;
      }
      @media screen and (min-width: ${vars.breakPointTablet}) {
        margin: 0 auto;
      }
    }
    .ais-InfiniteHits-loadMore--disabled {
      display: none;
    }
  }
`;

export default StyledContentHub;
